// Dark mode keeps the brand purple exactly as in light mode. The page is
// darkened with a CSS invert filter, which would otherwise turn every purple
// element (buttons, badges, active nav, totals) into light lavender. Rather
// than fix each one by hand across the whole site, this scans the inverted
// content for brand-purple elements and tags them so a counter-invert in CSS
// (.ew-keep-color) restores their original colour.

const SCOPE_SELECTOR = '.ewDarkScope, .homeContent';
const KEEP_CLASS = 'ew-keep-color';

// Is an "rgb(...)" / "rgba(...)" string a saturated purple/violet (the brand hue)?
function isBrandPurple(colorStr) {
  if (!colorStr) return false;
  const m = colorStr.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?/);
  if (!m) return false;
  const r = +m[1], g = +m[2], b = +m[3];
  const a = m[4] === undefined ? 1 : +m[4];
  if (a < 0.2) return false; // effectively transparent
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (max === 0) return false;
  const s = d / max;
  if (s < 0.28) return false; // greys / near-white are not brand colour
  let h;
  if (d === 0) h = 0;
  else if (max === r) h = (((g - b) / d) % 6 + 6) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  return h >= 250 && h <= 290; // purple / violet
}

// A dark, opaque background (e.g. a navy/black button or CTA) is already
// dark-themed, so inverting it is wrong — keep it too.
function isDarkOpaque(colorStr) {
  if (!colorStr) return false;
  const m = colorStr.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?/);
  if (!m) return false;
  const r = +m[1], g = +m[2], b = +m[3];
  const a = m[4] === undefined ? 1 : +m[4];
  if (a < 0.6) return false; // faint tints invert fine
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum < 0.22;
}

function keepAsIs(colorStr) {
  return isBrandPurple(colorStr) || isDarkOpaque(colorStr);
}

// Does this element directly contain visible (non-whitespace) text?
function hasOwnText(el) {
  for (let n = el.firstChild; n; n = n.nextSibling) {
    if (n.nodeType === 3 && n.nodeValue && n.nodeValue.trim()) return true;
  }
  return false;
}

function elementIsPurple(el) {
  const cs = window.getComputedStyle(el);
  // Gradient used as TEXT fill (background-clip: text) is text, not a surface —
  // it must invert to light for readability, so never keep it.
  const clip = (cs.getPropertyValue('-webkit-background-clip') || cs.backgroundClip || '');
  if (clip.indexOf('text') !== -1) return false;
  if (keepAsIs(cs.backgroundColor)) return true;
  const bi = cs.backgroundImage;
  if (bi && bi.indexOf('gradient') !== -1) {
    const rgbs = bi.match(/rgba?\([^)]*\)/g) || [];
    if (rgbs.some(keepAsIs)) return true;
  }
  // Brand-purple TEXT (eyebrows, labels, links). Only on a leaf that owns its
  // text, so the counter-invert never nests (color is inherited, so tagging a
  // container would double-invert its children).
  if (el.children.length === 0 && hasOwnText(el) && isBrandPurple(cs.color)) return true;
  return false;
}

export function refreshDarkPurple() {
  try {
    const on = document.documentElement.classList.contains('ew-dark');
    if (!on) {
      document.querySelectorAll('.' + KEEP_CLASS).forEach((el) => el.classList.remove(KEEP_CLASS));
      return;
    }
    document.querySelectorAll(SCOPE_SELECTOR).forEach((scope) => {
      scope.querySelectorAll('*').forEach((el) => {
        // Sections that opt out (rendered as light mode) handle themselves.
        if (el.closest('.ewKeepSection')) {
          if (el.classList.contains(KEEP_CLASS)) el.classList.remove(KEEP_CLASS);
          return;
        }
        if (elementIsPurple(el)) el.classList.add(KEEP_CLASS);
        else if (el.classList.contains(KEEP_CLASS)) el.classList.remove(KEEP_CLASS);
      });
    });
  } catch (e) { /* never break the page over styling */ }
}

let observer = null;
let timer = null;
const schedule = () => {
  if (timer) return;
  timer = setTimeout(() => { timer = null; refreshDarkPurple(); }, 150);
};

export function startDarkPurpleWatch() {
  refreshDarkPurple();
  if (observer) return;
  try {
    observer = new MutationObserver(schedule);
    // attributes:false so our own class changes don't retrigger the observer
    observer.observe(document.body, { childList: true, subtree: true, attributes: false });
  } catch (e) { /* ignore */ }
}

export function stopDarkPurpleWatch() {
  if (observer) { observer.disconnect(); observer = null; }
  if (timer) { clearTimeout(timer); timer = null; }
  document.querySelectorAll('.' + KEEP_CLASS).forEach((el) => el.classList.remove(KEEP_CLASS));
}
